import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SEOHead from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Check, ArrowRight, Loader2, X, Euro, Users, Bell, FileText, Shield } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAuth } from "@/hooks/useAuth";
import { useSubscription } from "@/hooks/useSubscription";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { SUBSCRIPTION_TIERS } from "@/lib/subscriptions";
import LanguageSelector from "@/components/LanguageSelector";

type PlanKey = "nazionaleBase" | "nazionaleProfessional" | "europaBase" | "europaProfessional";

const planDetails: Record<
  PlanKey,
  {
    name: string;
    users: string;
    description: string;
    idealFor: string;
    features: string[];
  }
> = {
  nazionaleBase: {
    name: "Piano Nazionale Base",
    users: "1 utente",
    description: "Monitoraggio nel tuo paese con ricerche illimitate e funzionalità essenziali.",
    idealFor: "Freelancer e piccole imprese",
    features: [
      "Ricerche giornaliere illimitate",
      "Copertura nazionale",
      "3 monitoraggi attivi",
      "Alert email standard",
      "Storico 30 giorni",
    ],
  },
  nazionaleProfessional: {
    name: "Piano Nazionale Professional",
    users: "3 utenti",
    description: "Monitoraggio nazionale completo con esportazione dati e supporto prioritario.",
    idealFor: "PMI e team di gara",
    features: [
      "Ricerche giornaliere illimitate",
      "Copertura nazionale",
      "Monitoraggi illimitati",
      "Alert email avanzati",
      "Esportazione CSV/Excel",
      "Storico completo",
      "Supporto prioritario",
    ],
  },
  europaBase: {
    name: "Piano Europa Base",
    users: "2 utenti",
    description: "Monitoraggio in tutti i paesi UE con funzionalità essenziali.",
    idealFor: "Aziende con mercato europeo",
    features: [
      "Ricerche giornaliere illimitate",
      "Tutti i 27 paesi UE",
      "5 monitoraggi attivi",
      "Alert email standard",
      "Storico 30 giorni",
    ],
  },
  europaProfessional: {
    name: "Piano Europa Professional",
    users: "5 utenti",
    description: "Monitoraggio europeo completo con tutte le funzionalità avanzate.",
    idealFor: "Grandi aziende e consorzi",
    features: [
      "Ricerche giornaliere illimitate",
      "Tutti i 27 paesi UE",
      "Monitoraggi illimitati",
      "Alert email avanzati",
      "Esportazione CSV/Excel",
      "Storico completo",
      "Supporto prioritario",
    ],
  },
};

export default function Prezzi() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { productId: currentProductId } = useSubscription();
  const { toast } = useToast();
  const [loadingPlan, setLoadingPlan] = useState<PlanKey | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (searchParams.get("checkout") === "cancelled") {
      toast({
        title: "Checkout annullato",
        description: "Il processo di pagamento è stato annullato. Puoi riprovare quando vuoi.",
      });
      searchParams.delete("checkout");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, toast]);

  const siteUrl = typeof window !== "undefined" ? window.location.origin : "https://tedmonitor.eu";

  const plans = useMemo(
    () => [
      {
        key: "nazionaleBase" as PlanKey,
        price: SUBSCRIPTION_TIERS.nazionaleBase.price,
        priceId: SUBSCRIPTION_TIERS.nazionaleBase.priceId,
        productId: SUBSCRIPTION_TIERS.nazionaleBase.productId,
        highlight: false,
      },
      {
        key: "nazionaleProfessional" as PlanKey,
        price: SUBSCRIPTION_TIERS.nazionaleProfessional.price,
        priceId: SUBSCRIPTION_TIERS.nazionaleProfessional.priceId,
        productId: SUBSCRIPTION_TIERS.nazionaleProfessional.productId,
        highlight: true,
      },
      {
        key: "europaBase" as PlanKey,
        price: SUBSCRIPTION_TIERS.europaBase.price,
        priceId: SUBSCRIPTION_TIERS.europaBase.priceId,
        productId: SUBSCRIPTION_TIERS.europaBase.productId,
        highlight: false,
      },
      {
        key: "europaProfessional" as PlanKey,
        price: SUBSCRIPTION_TIERS.europaProfessional.price,
        priceId: SUBSCRIPTION_TIERS.europaProfessional.priceId,
        productId: SUBSCRIPTION_TIERS.europaProfessional.productId,
        highlight: false,
      },
    ],
    [],
  );

  const handleSubscribe = async (plan: (typeof plans)[0]) => {
    if (!user) {
      navigate(`/auth?plan=${plan.key}`);
      return;
    }

    if (currentProductId === plan.productId) {
      toast({
        title: "Piano attuale",
        description: "Sei già abbonato a questo piano",
      });
      return;
    }

    setLoadingPlan(plan.key);

    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { priceId: plan.priceId, country: "IT" },
      });

      if (error) throw error;

      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (error: any) {
      console.error("Checkout error:", error);
      toast({
        variant: "destructive",
        title: "Errore",
        description: error.message || "Errore durante il checkout",
      });
    } finally {
      setLoadingPlan(null);
    }
  };

  const pricingStructuredData = useMemo(() => {
    const offers = plans.map((p) => ({
      "@type": "Offer",
      name: planDetails[p.key].name,
      price: String(p.price),
      priceCurrency: "EUR",
      url: `${siteUrl}/it/prezzi`,
      availability: "https://schema.org/InStock",
      category: "subscription",
    }));

    return {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Prezzi e Piani - TED Monitor",
      description:
        "Scegli il piano TED Monitor per il monitoraggio bandi pubblici europei: alert, ricerca avanzata, copertura multi-paese. Prezzi mensili trasparenti.",
      url: `${siteUrl}/it/prezzi`,
      inLanguage: "it",
      mainEntity: {
        "@type": "OfferCatalog",
        name: "Piani TED Monitor",
        itemListElement: offers,
      },
      isPartOf: {
        "@type": "WebSite",
        name: "TED Monitor",
        url: siteUrl,
      },
    };
  }, [plans, siteUrl]);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Prezzi"
        description="Piani e prezzi di TED Monitor: monitoraggio appalti UE su TED, alert, ricerca avanzata e copertura multi-paese. Prezzi mensili trasparenti."
        path="/it/prezzi"
        alternates={[
          { lang: "it", href: "https://tedmonitor.eu/it/prezzi" },
          { lang: "en", href: "https://tedmonitor.eu/pricing" },
          { lang: "x-default", href: "https://tedmonitor.eu/pricing" },
        ]}
      />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify(pricingStructuredData)}</script>
      </Helmet>

      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-mark.png" alt="TED Monitor" className="h-8 w-8" />
            <span className="font-semibold text-lg text-primary">TED Monitor</span>
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSelector />
            <Button asChild>
              <Link to="/auth">Accedi</Link>
            </Button>
          </div>
        </div>
      </header>

      <main>
        {/* Hero Section */}
        <section className="py-8 md:py-12 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto px-4 max-w-5xl">
            <h1 className="text-3xl md:text-4xl font-bold text-primary mb-4 leading-tight text-center">
              Piani e Prezzi TED Monitor
            </h1>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed text-center max-w-2xl mx-auto">
              Scegli il piano più adatto alle tue esigenze di monitoraggio bandi pubblici europei. Prezzi mensili
              trasparenti, upgrade in qualsiasi momento.
            </p>

            {/* Internal links */}
            <div className="mt-6 flex flex-wrap gap-2 justify-center">
              <Link
                to="/it/monitoraggio-bandi-ue"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Monitoraggio bandi UE
              </Link>
              <Link
                to="/it/alert-bandi-ted"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Alert bandi TED
              </Link>
              <Link
                to="/it/software-appalti-pubblici-europei"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Software appalti europei
              </Link>
              <Link
                to="/it/conformita-sicurezza"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Conformità e sicurezza
              </Link>
            </div>
          </div>
        </section>

        {/* Pricing Cards */}
        <section className="py-8 md:py-10">
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {plans.map((p) => {
                const details = planDetails[p.key];
                return (
                  <Card
                    key={p.key}
                    className={[
                      "bg-card/80 backdrop-blur-sm",
                      p.highlight ? "border-primary shadow-md shadow-primary/15" : "border-border",
                    ].join(" ")}
                  >
                    <CardContent className="p-5 flex flex-col h-full">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="text-sm font-semibold text-foreground">{details.name}</div>
                          <div className="mt-1 text-xs text-muted-foreground">{details.users}</div>
                        </div>
                        {p.highlight && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                            Consigliato
                          </span>
                        )}
                      </div>

                      <div className="mt-4">
                        <div className="flex items-end gap-1">
                          <div className="text-2xl font-bold text-foreground">€{p.price}</div>
                          <div className="text-xs text-muted-foreground">/mese</div>
                        </div>
                        <div className="mt-1 text-[10px] text-muted-foreground">Rinnovo automatico mensile</div>
                        <div className="mt-2 text-xs text-muted-foreground leading-relaxed">{details.description}</div>
                      </div>

                      <div className="mt-4 flex-grow">
                        <div className="text-[11px] font-medium text-foreground">Funzionalità incluse</div>
                        <ul className="mt-2 space-y-1.5">
                          {details.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-[11px] text-muted-foreground">
                              <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>

                        <div className="mt-4 text-[11px] text-muted-foreground">
                          <span className="font-medium text-foreground">Ideale per:</span> {details.idealFor}
                        </div>
                      </div>

                      <div className="mt-5 pt-4 border-t border-border/60">
                        {currentProductId === p.productId ? (
                          <Button className="w-full h-9 text-sm" variant="secondary" disabled>
                            Piano attuale
                          </Button>
                        ) : (
                          <Button
                            className="w-full h-9 text-sm group"
                            variant={p.highlight ? "default" : "outline"}
                            onClick={() => handleSubscribe(p)}
                            disabled={loadingPlan === p.key}
                          >
                            {loadingPlan === p.key ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                Abbonati
                                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
                              </>
                            )}
                          </Button>
                        )}

                        <div className="mt-2 text-[10px] text-muted-foreground text-center">
                          Accesso gratuito disponibile (3 ricerche/giorno)
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-8 md:py-10 bg-muted/30">
          <div className="container mx-auto px-4 max-w-5xl">
            <h2 className="text-xl font-bold text-foreground text-center mb-6">Confronta i Piani</h2>

            <div className="overflow-x-auto">
              <Table className="w-full">
                <TableHeader>
                  <TableRow className="border-border/50">
                    <TableHead className="text-xs font-medium text-muted-foreground w-[180px]">Funzionalità</TableHead>
                    <TableHead className="text-center text-xs font-medium text-muted-foreground">Free</TableHead>
                    {plans.map((p) => (
                      <TableHead
                        key={p.key}
                        className={`text-center text-xs font-medium ${p.highlight ? "text-primary" : "text-foreground"}`}
                      >
                        {planDetails[p.key].name.replace("Piano ", "")}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {/* Daily Searches */}
                  <TableRow className="border-border/30">
                    <TableCell className="text-xs text-muted-foreground font-medium">Ricerche giornaliere</TableCell>
                    <TableCell className="text-center text-xs">3</TableCell>
                    <TableCell className="text-center text-xs">Illimitate</TableCell>
                    <TableCell className="text-center text-xs">Illimitate</TableCell>
                    <TableCell className="text-center text-xs">Illimitate</TableCell>
                    <TableCell className="text-center text-xs">Illimitate</TableCell>
                  </TableRow>

                  {/* Results per search */}
                  <TableRow className="border-border/30">
                    <TableCell className="text-xs text-muted-foreground font-medium">Risultati per ricerca</TableCell>
                    <TableCell className="text-center text-xs">3</TableCell>
                    <TableCell className="text-center text-xs">Illimitati</TableCell>
                    <TableCell className="text-center text-xs">Illimitati</TableCell>
                    <TableCell className="text-center text-xs">Illimitati</TableCell>
                    <TableCell className="text-center text-xs">Illimitati</TableCell>
                  </TableRow>

                  {/* Scope */}
                  <TableRow className="border-border/30">
                    <TableCell className="text-xs text-muted-foreground font-medium">Copertura geografica</TableCell>
                    <TableCell className="text-center text-xs">27 paesi UE</TableCell>
                    <TableCell className="text-center text-xs">Nazionale</TableCell>
                    <TableCell className="text-center text-xs">Nazionale</TableCell>
                    <TableCell className="text-center text-xs">27 paesi UE</TableCell>
                    <TableCell className="text-center text-xs">27 paesi UE</TableCell>
                  </TableRow>

                  {/* Users */}
                  <TableRow className="border-border/30">
                    <TableCell className="text-xs text-muted-foreground font-medium">Utenti inclusi</TableCell>
                    <TableCell className="text-center text-xs">1</TableCell>
                    <TableCell className="text-center text-xs">1</TableCell>
                    <TableCell className="text-center text-xs">3</TableCell>
                    <TableCell className="text-center text-xs">2</TableCell>
                    <TableCell className="text-center text-xs">5</TableCell>
                  </TableRow>

                  {/* Monitorings */}
                  <TableRow className="border-border/30">
                    <TableCell className="text-xs text-muted-foreground font-medium">Monitoraggi attivi</TableCell>
                    <TableCell className="text-center text-xs">1</TableCell>
                    <TableCell className="text-center text-xs">3</TableCell>
                    <TableCell className="text-center text-xs">Illimitati</TableCell>
                    <TableCell className="text-center text-xs">5</TableCell>
                    <TableCell className="text-center text-xs">Illimitati</TableCell>
                  </TableRow>

                  {/* History */}
                  <TableRow className="border-border/30">
                    <TableCell className="text-xs text-muted-foreground font-medium">Storico risultati</TableCell>
                    <TableCell className="text-center text-xs text-muted-foreground">Solo sessione</TableCell>
                    <TableCell className="text-center text-xs">30 giorni</TableCell>
                    <TableCell className="text-center text-xs">Completo</TableCell>
                    <TableCell className="text-center text-xs">30 giorni</TableCell>
                    <TableCell className="text-center text-xs">Completo</TableCell>
                  </TableRow>

                  {/* Email Alerts */}
                  <TableRow className="border-border/30">
                    <TableCell className="text-xs text-muted-foreground font-medium">Alert email</TableCell>
                    <TableCell className="text-center">
                      <X className="w-4 h-4 text-muted-foreground/50 mx-auto" />
                    </TableCell>
                    <TableCell className="text-center text-xs">Standard</TableCell>
                    <TableCell className="text-center text-xs">Avanzati</TableCell>
                    <TableCell className="text-center text-xs">Standard</TableCell>
                    <TableCell className="text-center text-xs">Avanzati</TableCell>
                  </TableRow>

                  {/* Export */}
                  <TableRow className="border-border/30">
                    <TableCell className="text-xs text-muted-foreground font-medium">Esportazione CSV/Excel</TableCell>
                    <TableCell className="text-center">
                      <X className="w-4 h-4 text-muted-foreground/50 mx-auto" />
                    </TableCell>
                    <TableCell className="text-center">
                      <X className="w-4 h-4 text-muted-foreground/50 mx-auto" />
                    </TableCell>
                    <TableCell className="text-center">
                      <Check className="w-4 h-4 text-primary mx-auto" />
                    </TableCell>
                    <TableCell className="text-center">
                      <X className="w-4 h-4 text-muted-foreground/50 mx-auto" />
                    </TableCell>
                    <TableCell className="text-center">
                      <Check className="w-4 h-4 text-primary mx-auto" />
                    </TableCell>
                  </TableRow>

                  {/* Support */}
                  <TableRow className="border-border/30">
                    <TableCell className="text-xs text-muted-foreground font-medium">Supporto prioritario</TableCell>
                    <TableCell className="text-center">
                      <X className="w-4 h-4 text-muted-foreground/50 mx-auto" />
                    </TableCell>
                    <TableCell className="text-center">
                      <X className="w-4 h-4 text-muted-foreground/50 mx-auto" />
                    </TableCell>
                    <TableCell className="text-center">
                      <Check className="w-4 h-4 text-primary mx-auto" />
                    </TableCell>
                    <TableCell className="text-center">
                      <X className="w-4 h-4 text-muted-foreground/50 mx-auto" />
                    </TableCell>
                    <TableCell className="text-center">
                      <Check className="w-4 h-4 text-primary mx-auto" />
                    </TableCell>
                  </TableRow>

                  {/* Price */}
                  <TableRow className="border-border/30 bg-muted/30">
                    <TableCell className="text-xs text-foreground font-semibold">Prezzo</TableCell>
                    <TableCell className="text-center font-bold text-muted-foreground">Gratis</TableCell>
                    {plans.map((p) => (
                      <TableCell
                        key={p.key}
                        className={`text-center font-bold ${p.highlight ? "text-primary" : "text-foreground"}`}
                      >
                        €{p.price}/mese
                      </TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </div>
        </section>

        {/* Why Choose Section */}
        <section className="py-8 md:py-10">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-xl font-semibold text-foreground text-center mb-6">Perché scegliere TED Monitor</h2>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="text-center p-4">
                <Bell className="h-8 w-8 text-primary mx-auto mb-3" />
                <h3 className="font-medium text-foreground mb-2">Alert in tempo reale</h3>
                <p className="text-sm text-muted-foreground">
                  Ricevi notifiche immediate sui nuovi bandi che corrispondono ai tuoi criteri di ricerca.
                </p>
              </div>
              <div className="text-center p-4">
                <Shield className="h-8 w-8 text-primary mx-auto mb-3" />
                <h3 className="font-medium text-foreground mb-2">Dati ufficiali TED</h3>
                <p className="text-sm text-muted-foreground">
                  Accesso diretto ai dati della Gazzetta Ufficiale dell'Unione Europea.
                </p>
              </div>
              <div className="text-center p-4">
                <Users className="h-8 w-8 text-primary mx-auto mb-3" />
                <h3 className="font-medium text-foreground mb-2">Team collaboration</h3>
                <p className="text-sm text-muted-foreground">
                  Condividi monitoraggi e risultati con il tuo team di lavoro.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-8 md:py-10 bg-primary/5">
          <div className="container mx-auto px-4 max-w-2xl text-center">
            <h2 className="text-xl font-semibold text-foreground mb-3">Inizia subito gratuitamente</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Registrati per accedere a 3 ricerche giornaliere gratuite. Nessuna carta di credito richiesta.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button asChild>
                <Link to="/auth" className="gap-2">
                  Registrati gratis
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/it/monitoraggio-bandi-ue">Scopri di più</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t py-8 bg-card">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-xs text-muted-foreground text-center md:text-left">
              TED Monitor è un servizio indipendente basato sui dati pubblici di TED (Tenders Electronic Daily),
              Supplemento alla Gazzetta Ufficiale dell'Unione Europea.
            </div>
            <nav className="flex flex-wrap gap-4 text-xs justify-center">
              <Link to="/privacy-policy" className="text-muted-foreground hover:text-foreground">
                Privacy
              </Link>
              <Link to="/cookie-policy" className="text-muted-foreground hover:text-foreground">
                Cookie
              </Link>
              <Link to="/terms-conditions" className="text-muted-foreground hover:text-foreground">
                Termini
              </Link>
              <Link to="/data-source" className="text-muted-foreground hover:text-foreground">
                Fonte dati
              </Link>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
}
